import { useState, useEffect } from 'react';
import { trpc } from '../../api/trpc';
import { useAuth } from '../../hooks/useAuth';

type Provider = 'entra' | 'okta';

interface EntraConfigData {
  entraTenantId?: string;
  allowedDomains?: string[];
  defaultRole?: 'admin' | 'analyst';
  enabled?: boolean;
}

interface OktaConfigData {
  issuer?: string;
  clientId?: string;
  audience?: string;
  allowedDomains?: string[];
  defaultRole?: 'admin' | 'analyst';
  jitProvisioning?: boolean;
  enabled?: boolean;
}

export function IdpConfig() {
  const { tenantId } = useAuth();
  const [provider, setProvider] = useState<Provider | null>(null);

  // ── Entra state ─────────────────────────────────────────────────────────
  const { data: entraConfig, refetch: refetchEntra } = trpc.auth.getIdpConfigForTenant.useQuery(undefined, { enabled: !!tenantId });
  const entraUpsert = trpc.auth.upsertIdpConfig.useMutation();
  const [entraTenantId, setEntraTenantId] = useState('');
  const [entraAllowedDomains, setEntraAllowedDomains] = useState('');
  const [entraDefaultRole, setEntraDefaultRole] = useState<'admin' | 'analyst'>('analyst');
  const [entraEnabled, setEntraEnabled] = useState(false);
  const [entraSaved, setEntraSaved] = useState(false);

  useEffect(() => {
    if (entraConfig) {
      const cfg = entraConfig as EntraConfigData;
      setEntraTenantId(cfg.entraTenantId ?? '');
      setEntraAllowedDomains((cfg.allowedDomains ?? []).join(', '));
      setEntraDefaultRole(cfg.defaultRole ?? 'analyst');
      setEntraEnabled(cfg.enabled ?? false);
    }
  }, [entraConfig]);

  // ── Okta state ──────────────────────────────────────────────────────────
  const { data: oktaConfig, refetch: refetchOkta } = trpc.auth.getOktaIdpConfig.useQuery(undefined, { enabled: !!tenantId });
  const oktaUpsert = trpc.auth.upsertOktaIdpConfig.useMutation();
  const [oktaIssuer, setOktaIssuer] = useState('');
  const [oktaClientId, setOktaClientId] = useState('');
  const [oktaClientSecret, setOktaClientSecret] = useState('');
  const [oktaAudience, setOktaAudience] = useState('');
  const [oktaAllowedDomains, setOktaAllowedDomains] = useState('');
  const [oktaDefaultRole, setOktaDefaultRole] = useState<'admin' | 'analyst'>('analyst');
  const [oktaJit, setOktaJit] = useState(true);
  const [oktaEnabled, setOktaEnabled] = useState(false);
  const [oktaSaved, setOktaSaved] = useState(false);

  useEffect(() => {
    if (oktaConfig) {
      const cfg = oktaConfig as OktaConfigData;
      setOktaIssuer(cfg.issuer ?? '');
      setOktaClientId(cfg.clientId ?? '');
      setOktaAudience(cfg.audience ?? '');
      setOktaAllowedDomains((cfg.allowedDomains ?? []).join(', '));
      setOktaDefaultRole(cfg.defaultRole ?? 'analyst');
      setOktaJit(cfg.jitProvisioning ?? true);
      setOktaEnabled(cfg.enabled ?? false);
    }
  }, [oktaConfig]);

  // Auto-select provider based on which is configured
  useEffect(() => {
    if (provider) return;
    if (entraConfig && (entraConfig as EntraConfigData).entraTenantId) setProvider('entra');
    else if (oktaConfig && (oktaConfig as OktaConfigData).issuer) setProvider('okta');
  }, [entraConfig, oktaConfig, provider]);

  // ── Shared fields ───────────────────────────────────────────────────────
  const allowedDomains = provider === 'entra' ? entraAllowedDomains : oktaAllowedDomains;
  const setAllowedDomains = provider === 'entra' ? setEntraAllowedDomains : setOktaAllowedDomains;
  const defaultRole = provider === 'entra' ? entraDefaultRole : oktaDefaultRole;
  const setDefaultRole = provider === 'entra' ? setEntraDefaultRole : setOktaDefaultRole;
  const enabled = provider === 'entra' ? entraEnabled : oktaEnabled;
  const setEnabled = provider === 'entra' ? setEntraEnabled : setOktaEnabled;
  const saving = provider === 'entra' ? entraUpsert.isPending : oktaUpsert.isPending;
  const saved = provider === 'entra' ? entraSaved : oktaSaved;

  async function handleSave() {
    if (provider === 'entra') {
      setEntraSaved(false);
      await entraUpsert.mutateAsync({
        entraTenantId,
        allowedDomains: entraAllowedDomains.split(',').map((d) => d.trim()).filter(Boolean),
        defaultRole: entraDefaultRole,
        enabled: entraEnabled,
      });
      setEntraSaved(true);
      void refetchEntra();
    } else if (provider === 'okta') {
      setOktaSaved(false);
      await oktaUpsert.mutateAsync({
        issuer: oktaIssuer,
        clientId: oktaClientId,
        ...(oktaClientSecret ? { clientSecret: oktaClientSecret } : {}),
        audience: oktaAudience || oktaClientId,
        allowedDomains: oktaAllowedDomains.split(',').map((d) => d.trim()).filter(Boolean),
        defaultRole: oktaDefaultRole,
        jitProvisioning: oktaJit,
        enabled: oktaEnabled,
      });
      setOktaSaved(true);
      setOktaClientSecret('');
      void refetchOkta();
    }
  }

  return (
    <div>
      <h2 className="settings-section-title">Identity Provider</h2>
      <p className="settings-section-desc">Configure single sign-on for your organization.</p>

      {/* Provider selector */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, marginBottom: 20 }}>
        {([
          { id: 'entra' as Provider, label: 'Microsoft Entra ID', icon: (
            <svg width="14" height="14" viewBox="0 0 21 21" fill="none"><rect width="10" height="10" fill="#f25022"/><rect x="11" width="10" height="10" fill="#7fba00"/><rect y="11" width="10" height="10" fill="#00a4ef"/><rect x="11" y="11" width="10" height="10" fill="#ffb900"/></svg>
          )},
          { id: 'okta' as Provider, label: 'Okta', icon: (
            <svg width="14" height="14" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="50" fill="#007dc1"/><circle cx="50" cy="50" r="20" fill="#fff"/></svg>
          )},
        ]).map((p) => (
          <button
            key={p.id}
            onClick={() => setProvider(p.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 'var(--radius-md)',
              border: provider === p.id ? '1.5px solid var(--color-accent)' : '1px solid var(--color-border)',
              background: provider === p.id ? 'var(--color-bg-secondary)' : 'transparent',
              color: 'var(--color-text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
            }}
          >
            {p.icon} {p.label}
          </button>
        ))}
      </div>

      {!provider && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Select a provider to configure.</p>
      )}

      {provider && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 480 }}>
          {/* Provider-specific fields */}
          {provider === 'entra' && (
            <div className="form-field">
              <label htmlFor="entra-tenant">Entra Tenant ID (Directory ID)</label>
              <input
                id="entra-tenant"
                value={entraTenantId}
                onChange={(e) => setEntraTenantId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
            </div>
          )}

          {provider === 'okta' && (
            <>
              <div className="form-field">
                <label htmlFor="okta-issuer">Okta Issuer URL</label>
                <input id="okta-issuer" value={oktaIssuer} onChange={(e) => setOktaIssuer(e.target.value)} placeholder="https://your-org.okta.com" />
              </div>
              <div className="form-field">
                <label htmlFor="okta-client-id">OAuth Client ID</label>
                <input id="okta-client-id" value={oktaClientId} onChange={(e) => setOktaClientId(e.target.value)} placeholder="0oa..." />
              </div>
              <div className="form-field">
                <label htmlFor="okta-secret">OAuth Client Secret</label>
                <input id="okta-secret" type="password" value={oktaClientSecret} onChange={(e) => setOktaClientSecret(e.target.value)} placeholder={oktaConfig ? '••••••••  (leave blank to keep)' : 'Enter client secret'} />
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Stored securely. Never displayed after saving.</span>
              </div>
            </>
          )}

          {/* Shared fields */}
          <div className="form-field">
            <label htmlFor="idp-domains">Allowed Email Domains</label>
            <input
              id="idp-domains"
              value={allowedDomains}
              onChange={(e) => setAllowedDomains(e.target.value)}
              placeholder="company.com, subsidiary.com"
            />
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Comma-separated. Leave empty to allow all domains.</span>
          </div>
          <div className="form-field">
            <label htmlFor="idp-role">Default Role for New Users</label>
            <select id="idp-role" value={defaultRole} onChange={(e) => setDefaultRole(e.target.value as 'admin' | 'analyst')}>
              <option value="analyst">Analyst</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {provider === 'okta' && (
            <div className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input id="okta-jit" type="checkbox" checked={oktaJit} onChange={(e) => setOktaJit(e.target.checked)} style={{ width: 'auto' }} />
              <label htmlFor="okta-jit" style={{ margin: 0 }}>Enable JIT Provisioning</label>
            </div>
          )}

          <div className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input id="idp-enabled" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} style={{ width: 'auto' }} />
            <label htmlFor="idp-enabled" style={{ margin: 0 }}>Enable SSO</label>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn-primary" onClick={() => { void handleSave(); }} disabled={saving}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            {saved && <span style={{ color: 'var(--sev-success)', fontSize: 13 }}>Saved</span>}
          </div>
        </div>
      )}
    </div>
  );
}
